import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'wouter';
import { Package } from 'lucide-react';
import { AppShell } from '@/components/app-shell';
import { api, type OrdersResponse, type OrderItem } from '@/lib/api';

const STAGE_LABEL: Record<string, string> = {
  accepted: 'Accepted (awaiting PO/payment)',
  po_sent: 'Vendor PO placed',
  invoiced: 'Invoiced',
  paid: 'Paid',
  shipped: 'Shipped',
  delivered: 'Delivered',
  reviewed: 'Reviewed',
};
/** Not a stage: shipped or delivered while the proforma is still unpaid. */
const AWAITING_PAYMENT = 'awaiting_payment';
const STAGE_BADGE: Record<string, string> = {
  accepted: 'bg-blue-100 text-blue-900',
  po_sent: 'bg-indigo-100 text-indigo-900',
  invoiced: 'bg-amber-100 text-amber-900',
  paid: 'bg-emerald-100 text-emerald-900',
  shipped: 'bg-cyan-100 text-cyan-900',
  delivered: 'bg-emerald-100 text-emerald-900',
  reviewed: 'bg-emerald-100 text-emerald-900',
};

function AwaitingPaymentBadge({ o }: { o: OrderItem }) {
  if (!o.awaitingPayment) return null;
  return (
    <span className="inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-900">
      Awaiting payment
      {o.amountDueCents != null && o.amountDueCurrency ? ` · ${fmtMoney(o.amountDueCents, o.amountDueCurrency)}` : ''}
    </span>
  );
}

const CURRENCY_SYMBOL: Record<string, string> = {
  USD: '$', EUR: '€', GBP: '£', CAD: 'CA$', AUD: 'A$', JPY: '¥', CHF: 'CHF ', AED: 'AED ', SGD: 'S$',
};
function fmtMoney(cents: number, currency: string): string {
  const sym = CURRENCY_SYMBOL[currency] ?? `${currency} `;
  if (currency === 'JPY') return `${sym}${Math.round(cents / 100).toLocaleString('en-US')}`;
  return `${sym}${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function daysSince(iso: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000));
}

export function OrdersPage() {
  const { getToken } = useAuth();
  // ?filter=awaiting_payment (the dashboard links here) preselects the filter.
  const [stageFilter, setStageFilter] = useState<string>(() =>
    new URLSearchParams(window.location.search).get('filter') === AWAITING_PAYMENT ? AWAITING_PAYMENT : '',
  );
  useEffect(() => {
    const url = new URL(window.location.href);
    if (stageFilter === AWAITING_PAYMENT) url.searchParams.set('filter', AWAITING_PAYMENT);
    else url.searchParams.delete('filter');
    window.history.replaceState(null, '', url);
  }, [stageFilter]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['orders'],
    queryFn: () => api<OrdersResponse>('/api/orders', { tokenGetter: () => getToken() }),
    staleTime: 30_000,
  });

  const orders = useMemo(() => {
    const all = data?.orders ?? [];
    if (stageFilter === AWAITING_PAYMENT) return all.filter((o) => o.awaitingPayment);
    return stageFilter ? all.filter((o) => o.stage === stageFilter) : all;
  }, [data, stageFilter]);
  const awaitingCount = useMemo(() => (data?.orders ?? []).filter((o) => o.awaitingPayment).length, [data]);

  // Total value (only lines that have an accepted-quote value, grouped by currency).
  const totalsByCurrency = useMemo(() => {
    const m = new Map<string, number>();
    for (const o of orders) {
      if (o.valueCents != null && o.currency) m.set(o.currency, (m.get(o.currency) ?? 0) + o.valueCents);
    }
    return [...m.entries()];
  }, [orders]);

  return (
    <AppShell title="Orders" subtitle="Active orders from customer commitment through shipment — follow up so nothing slips">
      <div className="h-full overflow-auto px-6 py-6 space-y-4">
        {isLoading && <div className="card p-8 text-sm text-foreground-muted text-center">Loading orders…</div>}
        {isError && (
          <div className="card p-8 text-sm text-danger text-center">
            {(error as { message?: string })?.message ?? 'Failed to load orders'}
          </div>
        )}

        {data && (
          <section className="card overflow-hidden">
            <header className="px-5 py-3 border-b border-border flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Package className="w-4 h-4 text-primary" />
                <h2 className="font-semibold text-sm">Open orders</h2>
                <span className="pill pill-default">{orders.length}</span>
                {totalsByCurrency.map(([cur, cents]) => (
                  <span key={cur} className="text-xs text-foreground-muted font-mono">{fmtMoney(cents, cur)}</span>
                ))}
              </div>
              <select
                value={stageFilter}
                onChange={(e) => setStageFilter(e.target.value)}
                className="px-2 py-1 text-xs bg-background border border-border rounded"
              >
                <option value="">All stages</option>
                <option value={AWAITING_PAYMENT}>Shipped, awaiting payment{awaitingCount ? ` (${awaitingCount})` : ''}</option>
                {Object.entries(STAGE_LABEL).map(([s, label]) => (
                  <option key={s} value={s}>{label}</option>
                ))}
              </select>
            </header>

            <p className="px-5 py-2.5 text-xs text-foreground-muted bg-surface-muted/40 border-b border-border">
              Oldest first — the longer something sits, the more it needs a nudge. Delivered orders drop off once they are paid.
            </p>

            {orders.length === 0 ? (
              <div className="px-5 py-10 text-sm text-foreground-muted text-center italic">
                No open orders. 🎉
              </div>
            ) : (
              <>
              {/* Phones: one card per order. The table below is 820px wide, so on a phone
                  its Stage and Value columns were off-screen behind a sideways scroll. */}
              <ul className="divide-y divide-border xl:hidden">
                {orders.map((o: OrderItem) => {
                  const age = daysSince(o.stageChangedAt);
                  return (
                    <li key={o.lineId}>
                      <Link href={`/rfqs/${o.rfqPublicId}`} className="block px-4 py-3 active:bg-surface-muted/60">
                        <div className="flex items-start gap-2">
                          <span className="min-w-0 break-all font-mono text-sm font-semibold">{o.partNumber}</span>
                          {o.isAog && <span className="shrink-0 rounded bg-destructive px-1 py-0.5 text-[10px] font-bold text-destructive-foreground">AOG</span>}
                          {o.isFlagged && <span className="shrink-0">🚩</span>}
                        </div>
                        <div className="mt-0.5 break-words text-sm">{o.customerName}</div>
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          <span className={`inline-block max-w-full rounded px-1.5 py-0.5 text-xs ${STAGE_BADGE[o.stage] ?? 'bg-muted'}`}>
                            {STAGE_LABEL[o.stage] ?? o.stage}
                          </span>
                          <AwaitingPaymentBadge o={o} />
                        </div>
                        <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-foreground-muted">
                          <span className="font-mono">{o.qty} {o.uom}</span>
                          {o.valueCents != null && o.currency && <span className="font-mono">{fmtMoney(o.valueCents, o.currency)}</span>}
                          <span className={`font-mono ${age >= 7 ? 'font-semibold text-amber-700' : ''}`}>{age}d in stage</span>
                          {o.poNumber && <span>PO {o.poNumber}</span>}
                          <span className="font-mono">{o.rfqRef}</span>
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>

              <div className="hidden overflow-x-auto xl:block">
                <table className="w-full min-w-[820px] text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wide text-foreground-muted border-b border-border">
                      <th className="px-4 py-2 font-semibold">Customer</th>
                      <th className="px-4 py-2 font-semibold">Part</th>
                      <th className="px-4 py-2 font-semibold text-right">Qty</th>
                      <th className="px-4 py-2 font-semibold">Stage</th>
                      <th className="px-4 py-2 font-semibold text-right">Age</th>
                      <th className="px-4 py-2 font-semibold text-right">Value</th>
                      <th className="px-4 py-2 font-semibold">PO #</th>
                      <th className="px-4 py-2 font-semibold">RFQ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((o: OrderItem) => {
                      const age = daysSince(o.stageChangedAt);
                      return (
                        <tr key={o.lineId} className="border-b border-border hover:bg-surface-muted/40">
                          <td className="px-4 py-2.5">{o.customerName}</td>
                          <td className="px-4 py-2.5">
                            <span className="font-mono font-semibold">{o.partNumber}</span>
                            {o.isAog && <span className="ml-1.5 px-1 py-0.5 text-[9px] bg-destructive text-destructive-foreground rounded font-bold align-middle">AOG</span>}
                            {o.isFlagged && <span className="ml-1.5 align-middle">🚩</span>}
                          </td>
                          <td className="px-4 py-2.5 text-right font-mono">{o.qty} {o.uom}</td>
                          <td className="px-4 py-2.5">
                            <span className={`px-1.5 py-0.5 text-[11px] rounded ${STAGE_BADGE[o.stage] ?? 'bg-muted'}`}>{STAGE_LABEL[o.stage] ?? o.stage}</span>
                            {o.awaitingPayment && <span className="ml-1.5"><AwaitingPaymentBadge o={o} /></span>}
                          </td>
                          <td className={`px-4 py-2.5 text-right font-mono ${age >= 7 ? 'text-amber-700 font-semibold' : 'text-foreground-muted'}`}>
                            {age}d
                          </td>
                          <td className="px-4 py-2.5 text-right font-mono">{o.valueCents != null && o.currency ? fmtMoney(o.valueCents, o.currency) : '—'}</td>
                          <td className="px-4 py-2.5 text-xs text-foreground-muted">{o.poNumber ?? '—'}</td>
                          <td className="px-4 py-2.5">
                            <Link href={`/rfqs/${o.rfqPublicId}`} className="text-primary hover:underline font-mono text-xs">{o.rfqRef}</Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              </>
            )}
          </section>
        )}
      </div>
    </AppShell>
  );
}
